import { AppThunk } from "./store";
import {
  SET_ETH_TOKEN_PRICE,
  SET_UNI_POOL_24H_VOLUME,
  SET_UNI_POOL_24H_FEE,
  SET_UNI_POOL_TVL,
  SET_USER_ACTIVE_POSITIONS,
  SET_ACCOUNT_BALANCE,
  SET_USER_CLOSED_POSITIONS,
  GET_OPEN_POSITION_INFO,
  SET_CLOSED_POSITIONS,
  SET_ACTIVE_POSITIONS,
  CLEAR_USER_POSITIONS,
  setActivePositionIsFetched,
  setClosedPositionIsFetched,
  updateUnibotTvl,
  updateUnibotCumulativeFee,
} from "./factorySlice";
import {
  getUniV3PoolStatus,
  getUserActivePositions,
  getOpenPositionInfoFromLog,
  getArbitrumBlockNum,
  getUniV3PoolVolume,
} from "../utils/api/factoryV3Api";
import {
  getGinzaFactoryPositions,
  getGinzaBalancesSummary,
  getTokenPriceFromBinance,
  getGinzaFactoryIds,
  getGinzaFactorySummary,
  getAllUserClosedPositions,
} from "../utils/api/ginzaApi";
import { utils } from "ethers";

type tokenProps = {
  tokenSymbol: string;
  tokenFullName: string;
  tokenDecimal: number;
};

export const setUniPoolPriceAndTvl =
  (
    poolAddress: string,
    tokenA: tokenProps,
    tokenB: tokenProps,
    chainId: number
  ): AppThunk =>
  async (dispatch) => {
    const poolStatus = await getUniV3PoolStatus(
      poolAddress,
      tokenA,
      tokenB,
      chainId
    );

    if (poolStatus) {
      const { price, tvl } = poolStatus;
      dispatch(SET_ETH_TOKEN_PRICE(price));
      dispatch(SET_UNI_POOL_TVL({ poolAddress, tvl: +tvl }));
    }
  };

export const setUniPoolStatus =
  (
    timestamp: number,
    poolAddress: string,
    tokenA: tokenProps,
    tokenB: tokenProps,
    chainId: number
  ): AppThunk =>
  async (dispatch) => {
    const currTime = timestamp;
    const preTime = currTime - 60 * 60 * 24;

    // const currBlockNum = await getArbitrumBlockNum(currTime);
    const preBlockNum = await getArbitrumBlockNum(preTime);

    const currRes = await getUniV3PoolVolume(0, poolAddress);
    const preRes = await getUniV3PoolVolume(preBlockNum, poolAddress);

    const poolStatus = await getUniV3PoolStatus(
      poolAddress,
      tokenA,
      tokenB,
      chainId
    );

    if (currRes && preRes && poolStatus) {
      const { feeTier } = poolStatus;
      dispatch(
        SET_UNI_POOL_24H_VOLUME({
          poolAddress,
          volume: +currRes - +preRes,
        })
      );
      dispatch(
        SET_UNI_POOL_24H_FEE({
          poolAddress,
          fee: (+currRes - +preRes) * (feeTier / 1000000),
        })
      );
    }
  };

export const getFactoryPositions =
  (
    userAddress: string,
    factoryAddress: string,
    wantTokenAddress: string,
    wantTokenDecimal: number,
    borrowTokenAddress: string,
    borrowTokenDecimal: number,
    poolAddress: string,
    lendingPoolAddress: string,
    chainId: number,
    provider: any
  ): AppThunk =>
  async (dispatch) => {
    const positionInfos = await getUserActivePositions(
      userAddress,
      factoryAddress,
      wantTokenAddress,
      wantTokenDecimal,
      borrowTokenAddress,
      borrowTokenDecimal,
      poolAddress,
      lendingPoolAddress,
      chainId,
      provider
    );
    dispatch(
      SET_USER_ACTIVE_POSITIONS({ factoryAddress, positionInfos, chainId })
    );
  };

export const getPositionHistories =
  (
    accountAddress: string,
    factoryAddress: string,
    poolAddress: string,
    wantTokenDecimal: number,
    borrowTokenDecimal: number,
    chainId: number,
    provider: any
  ): AppThunk =>
  async (dispatch) => {
    try {
      const positions = await getAllUserClosedPositions(
        accountAddress,
        factoryAddress,
        poolAddress,
        wantTokenDecimal,
        borrowTokenDecimal,
        chainId,
        provider
      );
      dispatch(
        SET_USER_CLOSED_POSITIONS({ factoryAddress, positions, chainId })
      );
    } catch (error) {}
  };

export const clearUserRecords = (): AppThunk => async (dispatch) => {
  dispatch(CLEAR_USER_POSITIONS());
  dispatch(SET_ACCOUNT_BALANCE(0));
};

export const getPositionInfos =
  (
    accountAddress: string,
    factoryAddress: string,
    poolAddress: string,
    wantTokenDecimal: number,
    borrowTokenDecimal: number,
    chainId: number,
    provider: any
  ): AppThunk =>
  async (dispatch) => {
    try {
      const openPositionInfo = await getOpenPositionInfoFromLog(
        accountAddress,
        factoryAddress,
        poolAddress,
        wantTokenDecimal,
        borrowTokenDecimal,
        chainId,
        provider
      );
      dispatch(
        GET_OPEN_POSITION_INFO({
          factoryAddress,
          positionInfo: openPositionInfo,
        })
      );
    } catch (error) {}
  };

export const getAllClosePosition =
  (selectedChainId: number): AppThunk =>
  async (dispatch) => {
    dispatch(setClosedPositionIsFetched(false));
    try {
      const { records, totalRecord } = await getGinzaFactoryPositions(
        selectedChainId,
        "closed"
      );
      dispatch(
        SET_CLOSED_POSITIONS({
          chainId: selectedChainId,
          records: records,
          totalRecord: totalRecord,
        })
      );
    } catch (error) {}
    dispatch(setClosedPositionIsFetched(true));
  };

export const getAllOpenPosition =
  (selectedChainId: number): AppThunk =>
  async (dispatch) => {
    dispatch(setActivePositionIsFetched(false));
    try {
      const { records, totalRecord } = await getGinzaFactoryPositions(
        selectedChainId,
        "open"
      );
      dispatch(
        SET_ACTIVE_POSITIONS({
          chainId: selectedChainId,
          records: records,
          totalRecord: totalRecord,
        })
      );
    } catch (error) {}
    dispatch(setActivePositionIsFetched(true));
  };

export const getTvl =
  (chainId: number): AppThunk =>
  async (dispatch) => {
    let tvl = 0;
    try {
      const balances = await getGinzaBalancesSummary(chainId);
      await Promise.all(
        await balances.map(async (balance) => {
          try {
            const price = await getTokenPriceFromBinance(balance.symbol);
            tvl += +utils.formatUnits(
              (+balance.sum * +price).toString(),
              balance.decimal
            );
          } catch (e) {}
        })
      );
    } catch (error: any) {}
    dispatch(updateUnibotTvl({ chainId: chainId, tvl: tvl }));
  };

export const getCumulativeFee =
  (chainId: number): AppThunk =>
  async (dispatch) => {
    let cumulativeFee = 0;
    try {
      const ids = await getGinzaFactoryIds(chainId);
      await Promise.all(
        await ids.map(async (id: string) => {
          const {
            token_info: tokenInfo,
            close_position_borrow_token_total_fee: closedPositionBorrowTokenFee,
            close_position_want_token_total_fee: closedPositionWantTokenFee,
            open_position_borrow_token_total_fee: activePositionBorrowTokenFee,
            open_position_want_token_total_fee: activePositionWantTokenFee,
          } = await getGinzaFactorySummary(id);

          const borrowTokenPrice = await getTokenPriceFromBinance(
            tokenInfo.borrow_token.symbol
          );
          const wantTokenPrice = await getTokenPriceFromBinance(
            tokenInfo.want_token.symbol
          );

          try {
            const decimal = tokenInfo.borrow_token.decimal;
            const closedFee = utils.formatUnits(
              closedPositionBorrowTokenFee,
              decimal
            );
            const activeFee = utils.formatUnits(
              activePositionBorrowTokenFee,
              decimal
            );

            const borrowTokenFee = +closedFee + +activeFee * borrowTokenPrice;

            if (borrowTokenFee) {
              cumulativeFee += +borrowTokenFee;
            }
          } catch (e) {
            console.log(e);
          }

          try {
            const wantTokenFee = utils.formatUnits(
              +closedPositionWantTokenFee +
                +activePositionWantTokenFee * wantTokenPrice,
              tokenInfo.want_token.decimal
            );

            if (wantTokenFee) {
              cumulativeFee += +wantTokenFee;
            }
          } catch (e) {}
        })
      );
    } catch (error: any) {
      console.log(error);
    }
    dispatch(
      updateUnibotCumulativeFee({
        chainId: chainId,
        cumulativeFee: cumulativeFee,
      })
    );
  };
